import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../api';

const LedgerReport = () => {
  const [activeTab, setActiveTab] = useState('CUSTOMER'); // 'CUSTOMER' or 'SUPPLIER'
  const [salesData, setSalesData] = useState([]);
  const [purchasesData, setPurchasesData] = useState([]);
  const [creditNotesData, setCreditNotesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selected Party for modal
  const [selectedParty, setSelectedParty] = useState(null);

  // Delete Confirmation States
  const [deleteConfirmParty, setDeleteConfirmParty] = useState(null);
  const [isDeletingParty, setIsDeletingParty] = useState(false);
  const [deleteConfirmTx, setDeleteConfirmTx] = useState(null);
  const [isDeletingTx, setIsDeletingTx] = useState(false);
  const [isSavingLedger, setIsSavingLedger] = useState(false);

  const navigate = useNavigate();

  const fetchData = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      else setIsRefreshing(true);
      
      try {
        const [salesRes, purchasesRes, cnRes] = await Promise.all([
          fetch(apiUrl('/api/sales')).catch(() => null),
          fetch(apiUrl('/api/purchases')).catch(() => null),
          fetch(apiUrl('/api/credit-notes')).catch(() => null)
        ]);

        if (salesRes && salesRes.ok) {
          const sData = await salesRes.json();
          setSalesData(sData || []);
        }
        if (purchasesRes && purchasesRes.ok) {
          const pData = await purchasesRes.json();
          setPurchasesData(pData || []);
        }
        if (cnRes && cnRes.ok) {
          const cnData = await cnRes.json();
          setCreditNotesData(cnData || []);
        }
      } catch (e) {
        console.error('Error fetching ledger endpoints:', e);
      }
    } catch (error) {
      console.error('Failed to fetch ledger data', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh ledger data every 5 seconds for real-time sync with Near Expiry Credit Notes
    const interval = setInterval(() => {
      fetchData(true);
    }, 5000);

    const handleFocus = () => {
      fetchData(true);
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // --- Process Customers ---
  const customerParties = useMemo(() => {
    const map = new Map();
    (salesData || []).forEach(sale => {
      const name = sale.custName || 'Walk-in';
      const key = name.toUpperCase().trim().replace(/\s+/g, ' ');
      if (!map.has(key)) {
        map.set(key, { name, mobile: sale.custMob || '', totalBilled: 0, totalPaid: 0, totalReturns: 0, totalPending: 0, transactions: [] });
      }
      const party = map.get(key);
      party.transactions.push(sale);
      
      const amount = parseFloat(sale.netAmount || sale.amount || 0) || 0;
      party.totalBilled += amount;
      
      if (sale.paymentStatus === 'PAID' || sale.saleType === 'CASH' || sale.saleType === 'UPI') {
        party.totalPaid += amount;
      } else {
        party.totalPending += amount;
      }
    });
    
    return Array.from(map.values()).sort((a, b) => b.totalBilled - a.totalBilled);
  }, [salesData]);

  // --- Process Suppliers (With Credit Notes & Near Expiry Returns Auto-Sync) ---
  const supplierParties = useMemo(() => {
    const map = new Map();

    // 1. Load purchases
    (purchasesData || []).forEach(p => {
      const name = (p.supplierName || p.supplier || 'Unknown').trim();
      const key = name.toUpperCase().replace(/\s+/g, ' ');
      if (!map.has(key)) {
        map.set(key, { name, totalBilled: 0, totalPaid: 0, totalReturns: 0, cnReturns: 0, nearExpiryReturns: 0, totalPending: 0, transactions: [] });
      }
      const party = map.get(key);
      party.transactions.push({ ...p, isCreditNote: false });
      
      const amount = parseFloat(p.netAmount || p.totalAmount || p.grossAmount || 0) || 0;
      party.totalBilled += amount;
      
      if (p.paymentType === 'Cash' || p.paymentType === 'UPI' || p.status === 'Paid') {
        party.totalPaid += amount;
      }
    });

    // 2. Merge saved Credit Notes from Firestore
    (creditNotesData || []).forEach(cn => {
      const name = (cn.supplierName || 'Unknown').trim();
      const key = name.toUpperCase().replace(/\s+/g, ' ');
      if (!map.has(key)) {
        map.set(key, { name, totalBilled: 0, totalPaid: 0, totalReturns: 0, cnReturns: 0, nearExpiryReturns: 0, totalPending: 0, transactions: [] });
      }
      const party = map.get(key);
      party.transactions.push({ ...cn, isCreditNote: true });
      const returnAmt = parseFloat(cn.totalAmount || 0) || 0;
      party.cnReturns += returnAmt;
    });

    // 3. Compute Near Expiry stock returns directly from purchases for each party
    const today = new Date();
    const twoMonthsLater = new Date();
    twoMonthsLater.setMonth(today.getMonth() + 2);
    twoMonthsLater.setDate(28);

    Array.from(map.values()).forEach(party => {
      let neAmt = 0;
      const partyKey = party.name.toUpperCase().replace(/\s+/g, ' ');

      (purchasesData || []).forEach(p => {
        const pSupplier = (p.supplierName || p.supplier || 'Unknown').trim();
        if (pSupplier.toUpperCase().replace(/\s+/g, ' ') === partyKey) {
          if (p.items && Array.isArray(p.items)) {
            p.items.forEach(item => {
              if (item.exp) {
                const parts = item.exp.split('/');
                if (parts.length === 2) {
                  const month = parseInt(parts[0], 10) - 1;
                  let year = parseInt(parts[1], 10);
                  if (year < 100) year += 2000;
                  const expDate = new Date(year, month + 1, 0);

                  if (expDate <= twoMonthsLater) {
                    const qty = parseInt(item.qty, 10) || 0;
                    const rate = parseFloat(item.rate || item.mrp || 0) || 0;
                    const amount = parseFloat(item.amount) || (qty * rate);
                    neAmt += amount;
                  }
                }
              }
            });
          }
        }
      });

      party.nearExpiryReturns = neAmt;
      // Effective total returns is the max of saved credit notes vs near expiry return value (or combined if cnReturns > 0)
      party.totalReturns = Math.max(party.cnReturns, party.nearExpiryReturns);

      // If near expiry return value exists but no saved credit note transaction yet, add virtual CN entry for statement view
      if (party.cnReturns === 0 && party.nearExpiryReturns > 0) {
        party.transactions.push({
          id: `NE-${party.name}`,
          creditNoteNo: `CN-EXPIRY`,
          supplierName: party.name,
          date: new Date().toISOString().split('T')[0],
          totalAmount: party.nearExpiryReturns,
          reason: 'Near Expiry Stock Return Credit Value',
          isCreditNote: true,
          isNearExpiryAuto: true
        });
      }

      // 4. Compute Net Pending Due (Deducting Paid & Expiry Credit Note Returns)
      party.totalPending = Math.max(0, party.totalBilled - party.totalPaid - party.totalReturns);
    });
    
    return Array.from(map.values()).sort((a, b) => b.totalBilled - a.totalBilled);
  }, [purchasesData, creditNotesData]);

  // Filter based on active tab and search query
  const filteredParties = useMemo(() => {
    const list = activeTab === 'CUSTOMER' ? customerParties : supplierParties;
    if (!searchQuery.trim()) return list;
    
    const lowerSearch = searchQuery.toLowerCase();
    return list.filter(party => party.name.toLowerCase().includes(lowerSearch));
  }, [activeTab, customerParties, supplierParties, searchQuery]);

  // Overall totals for the current view
  const overallSummary = useMemo(() => {
    let totalBilled = 0, totalPaid = 0, totalReturns = 0, totalPending = 0;
    filteredParties.forEach(p => {
      totalBilled += p.totalBilled;
      totalPaid += p.totalPaid;
      totalReturns += (p.totalReturns || 0);
      totalPending += p.totalPending;
    });
    return { totalBilled, totalPaid, totalReturns, totalPending };
  }, [filteredParties]);

  const handlePrint = () => {
    window.print();
  };

  const handleSaveLedger = async () => {
    if (isSavingLedger) return;
    try {
      setIsSavingLedger(true);
      const payload = {
        date: new Date().toISOString(),
        customerParties,
        supplierParties,
        overallSummary
      };
      const res = await fetch(apiUrl('/api/ledger/save'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        alert('Ledger saved to Firestore successfully!');
      } else {
        alert(`Failed to save ledger to Firestore: ${data.details || data.error || res.statusText || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving ledger:', error);
      alert('Error saving ledger: ' + error.message);
    } finally {
      setIsSavingLedger(false);
    }
  };

  // Delete all records/transactions for a party
  const handleDeleteParty = async (party) => {
    if (!party) return;
    try {
      setIsDeletingParty(true);
      const txs = party.transactions || [];
      const partyKey = (party.name || '').trim().toUpperCase();

      // Collect all candidate IDs for optimistic filter
      const deletedIds = new Set(
        txs.flatMap(t => [t.id, t._id, t.docId, t.firestoreId, t.vchNo, t.invoiceNo].filter(Boolean).map(String))
      );

      // 1. Optimistically update local React state immediately so party disappears from table instantly!
      if (activeTab === 'CUSTOMER') {
        setSalesData(prev => prev.filter(s => {
          const sName = (s.custName || 'Walk-in').trim().toUpperCase();
          const sId = String(s.id || s._id || s.docId || s.vchNo || '');
          return sName !== partyKey && !deletedIds.has(sId);
        }));
      } else {
        setPurchasesData(prev => prev.filter(p => {
          const pName = (p.supplierName || p.supplier || 'Unknown').trim().toUpperCase();
          const pId = String(p.id || p._id || p.docId || p.invoiceNo || '');
          return pName !== partyKey && !deletedIds.has(pId);
        }));
        setCreditNotesData(prev => prev.filter(c => {
          const cName = (c.supplierName || 'Unknown').trim().toUpperCase();
          const cId = String(c.id || c._id || c.docId || c.creditNoteNo || '');
          return cName !== partyKey && !deletedIds.has(cId);
        }));
      }

      // 2. Perform backend deletes
      // 2a. Call bulk endpoint for total cleanup
      if (activeTab === 'CUSTOMER') {
        await fetch(apiUrl(`/api/sales/by-customer/${encodeURIComponent(party.name)}`), { method: 'DELETE' }).catch(() => null);
      } else {
        await fetch(apiUrl(`/api/purchases/by-supplier/${encodeURIComponent(party.name)}`), { method: 'DELETE' }).catch(() => null);
        await fetch(apiUrl(`/api/credit-notes/by-supplier/${encodeURIComponent(party.name)}`), { method: 'DELETE' }).catch(() => null);
      }

      // 2b. Also delete each transaction individually to ensure Firestore docs are completely purged
      await Promise.all(txs.map(async (tx) => {
        if (tx.isNearExpiryAuto) return;
        const targetId = tx.id || tx._id || tx.docId || tx.firestoreId || tx.vchNo || tx.invoiceNo;
        if (!targetId) return;
        try {
          if (tx.isCreditNote || tx.creditNoteNo) {
            await fetch(apiUrl(`/api/credit-notes/${targetId}`), { method: 'DELETE' });
          } else if (activeTab === 'CUSTOMER') {
            await fetch(apiUrl(`/api/sales/${targetId}`), { method: 'DELETE' });
          } else {
            await fetch(apiUrl(`/api/purchases/${targetId}`), { method: 'DELETE' });
          }
        } catch (e) {
          console.error('Error deleting single transaction during party deletion:', e);
        }
      }));

      // If supplier, also remove matching stockist party from master if present
      if (activeTab === 'SUPPLIER') {
        try {
          const stkRes = await fetch(apiUrl('/api/stockists'));
          if (stkRes.ok) {
            const stockists = await stkRes.json();
            const matchingStk = stockists.find(s => s.name && s.name.toUpperCase().trim() === party.name.toUpperCase().trim());
            if (matchingStk && matchingStk.id) {
              await fetch(apiUrl(`/api/stockists/${matchingStk.id}`), { method: 'DELETE' });
            }
          }
        } catch (e) {
          console.error('Error deleting stockist party master:', e);
        }
      }

      setDeleteConfirmParty(null);
      if (selectedParty && selectedParty.name === party.name) {
        setSelectedParty(null);
      }
      await fetchData(true);
    } catch (err) {
      console.error('Failed to delete party:', err);
      alert('Error deleting party: ' + err.message);
    } finally {
      setIsDeletingParty(false);
    }
  };

  // Delete a single transaction (invoice / credit note / sale)
  const handleDeleteTransaction = async (tx) => {
    if (!tx) return;
    if (tx.isNearExpiryAuto) {
      alert("This is an auto-calculated Near Expiry credit entry derived from purchase stock items. To remove it, delete or return the expired purchase invoice.");
      setDeleteConfirmTx(null);
      return;
    }

    try {
      setIsDeletingTx(true);
      const targetId = tx.id || tx._id || tx.docId || tx.firestoreId || tx.vchNo || tx.invoiceNo;
      if (!targetId) {
        alert("Cannot delete: Bill ID was not found.");
        return;
      }

      // 1. Call backend delete
      let res;
      if (tx.isCreditNote || tx.creditNoteNo) {
        res = await fetch(apiUrl(`/api/credit-notes/${targetId}`), { method: 'DELETE' });
      } else if (activeTab === 'CUSTOMER') {
        res = await fetch(apiUrl(`/api/sales/${targetId}`), { method: 'DELETE' });
      } else {
        res = await fetch(apiUrl(`/api/purchases/${targetId}`), { method: 'DELETE' });
      }

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || errJson.details || `Server returned error status ${res.status}`);
      }

      // 2. Optimistically update main table state immediately!
      const targetIdStr = String(targetId);
      if (activeTab === 'CUSTOMER') {
        setSalesData(prev => prev.filter(s => {
          const sId = String(s.id || s._id || s.docId || s.vchNo || '');
          return sId !== targetIdStr;
        }));
      } else if (tx.isCreditNote || tx.creditNoteNo) {
        setCreditNotesData(prev => prev.filter(c => {
          const cId = String(c.id || c._id || c.docId || c.creditNoteNo || '');
          return cId !== targetIdStr;
        }));
      } else {
        setPurchasesData(prev => prev.filter(p => {
          const pId = String(p.id || p._id || p.docId || p.invoiceNo || '');
          return pId !== targetIdStr;
        }));
      }

      setDeleteConfirmTx(null);

      // 3. Instantly update popup modal state
      if (selectedParty) {
        const remaining = (selectedParty.transactions || []).filter(t => {
          const tId = String(t.id || t._id || t.docId || t.vchNo || t.invoiceNo || '');
          return tId !== targetIdStr;
        });
        if (remaining.length === 0) {
          setSelectedParty(null);
        } else {
          let totalBilled = 0, totalPaid = 0, cnReturns = 0;
          remaining.forEach(t => {
            if (t.isCreditNote) {
              cnReturns += parseFloat(t.totalAmount || 0) || 0;
            } else if (activeTab === 'CUSTOMER') {
              const amt = parseFloat(t.netAmount || t.amount || 0) || 0;
              totalBilled += amt;
              if (t.paymentStatus === 'PAID' || t.saleType === 'CASH' || t.saleType === 'UPI') totalPaid += amt;
            } else {
              const amt = parseFloat(t.netAmount || t.totalAmount || t.grossAmount || 0) || 0;
              totalBilled += amt;
              if (t.paymentType === 'Cash' || t.paymentType === 'UPI' || t.status === 'Paid') totalPaid += amt;
            }
          });
          const totalReturns = Math.max(cnReturns, selectedParty.nearExpiryReturns || 0);
          const totalPending = Math.max(0, totalBilled - totalPaid - totalReturns);

          setSelectedParty(prev => ({
            ...prev,
            transactions: remaining,
            totalBilled,
            totalPaid,
            cnReturns,
            totalReturns,
            totalPending
          }));
        }
      }

      // 4. Background re-fetch to confirm persistent synchronization
      await fetchData(true);
    } catch (err) {
      console.error('Failed to delete transaction:', err);
      alert('Error deleting transaction: ' + err.message);
    } finally {
      setIsDeletingTx(false);
    }
  };

  return (
    <div className="h-full flex flex-col space-y-2.5 animate-in fade-in duration-500 px-2 md:px-4 pb-2 w-full overflow-hidden">
      {/* Top Header Row: Back + Title + Tabs + Save/Print */}
      <div className="flex flex-wrap items-center justify-between gap-2 shrink-0 bg-white p-2.5 rounded-2xl border border-slate-200/80 shadow-sm">
        <div className="flex items-center gap-2">
          <button 
            onClick={() => navigate('/dashboard')}
            className="p-1.5 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 transition-colors text-slate-600"
            title="Back to Dashboard"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
          </button>
          <div className="w-1.5 h-6 rounded-full bg-indigo-600"></div>
          <div>
            <h2 className="text-base md:text-lg font-black text-slate-800 tracking-tight leading-none">Ledger Report</h2>
            <p className="text-[10px] text-slate-400 font-bold mt-0.5">Customer & Supplier Statements</p>
          </div>
        </div>

        {/* Center: Tabs Switcher */}
        <div className="bg-slate-100 p-1 rounded-xl flex items-center gap-1 border border-slate-200/60 print:hidden">
          <button 
            onClick={() => { setActiveTab('CUSTOMER'); setSearchQuery(''); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs transition-all ${activeTab === 'CUSTOMER' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
            Customer Ledger ({customerParties.length})
          </button>
          <button 
            onClick={() => { setActiveTab('SUPPLIER'); setSearchQuery(''); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs transition-all ${activeTab === 'SUPPLIER' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
            Supplier Ledger ({supplierParties.length})
          </button>
        </div>

        {/* Right: Refresh, Auto-Sync, Save and Print Actions */}
        <div className="flex items-center gap-2">
          {/* Refresh Button */}
          <button 
            onClick={() => fetchData()}
            disabled={loading || isRefreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer active:scale-95 disabled:opacity-50"
            title="Refresh Ledger Data"
          >
            <svg className={`w-3.5 h-3.5 text-emerald-600 ${isRefreshing || loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            <span>{isRefreshing || loading ? 'Refreshing...' : 'Refresh'}</span>
          </button>

          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-[11px] font-bold">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span>Live Sync</span>
            {isRefreshing && (
              <svg className="w-3 h-3 text-emerald-600 animate-spin ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
            )}
          </div>
          <button 
            onClick={handleSaveLedger}
            disabled={isSavingLedger}
            className={`flex items-center gap-1.5 px-3 py-1.5 ${isSavingLedger ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white rounded-xl text-xs font-bold shadow-sm transition-all`}
          >
            {isSavingLedger ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path></svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path></svg>
            )}
            <span>{isSavingLedger ? 'Saving...' : 'Save'}</span>
          </button>
          <button 
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold shadow-sm transition-all"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
            Print
          </button>
        </div>
      </div>

      {/* Compact Controls & Summary Bar */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center shrink-0">
        {/* Search Bar */}
        <div className="md:col-span-4 relative print:hidden">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </div>
          <input 
            type="text" 
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:border-indigo-500 transition-all outline-none shadow-sm"
            placeholder={`Search ${activeTab === 'CUSTOMER' ? 'Customer' : 'Supplier'} by name...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 text-xs font-bold">×</button>
          )}
        </div>

        {/* KPI Badges */}
        <div className={`md:col-span-8 grid ${activeTab === 'SUPPLIER' ? 'grid-cols-4' : 'grid-cols-3'} gap-2`}>
          <div className="bg-white border border-slate-200/80 px-3 py-1.5 rounded-xl shadow-sm flex items-center justify-between">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total {activeTab === 'CUSTOMER' ? 'Billed' : 'Purchased'}</span>
            <span className="text-sm font-black text-slate-800">₹{overallSummary.totalBilled.toFixed(2)}</span>
          </div>
          <div className="bg-emerald-50/80 border border-emerald-200/80 px-3 py-1.5 rounded-xl shadow-sm flex items-center justify-between">
            <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Total Paid</span>
            <span className="text-sm font-black text-emerald-700">₹{overallSummary.totalPaid.toFixed(2)}</span>
          </div>
          {activeTab === 'SUPPLIER' && (
            <div className="bg-teal-50/80 border border-teal-200/80 px-3 py-1.5 rounded-xl shadow-sm flex items-center justify-between">
              <span className="text-[10px] font-bold text-teal-700 uppercase tracking-wider">Credit Notes / Returns (Cr)</span>
              <span className="text-sm font-black text-teal-700">₹{(overallSummary.totalReturns || 0).toFixed(2)}</span>
            </div>
          )}
          <div className="bg-rose-50/80 border border-rose-200/80 px-3 py-1.5 rounded-xl shadow-sm flex items-center justify-between">
            <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wider">Pending Due (Bal)</span>
            <span className="text-sm font-black text-rose-700">₹{overallSummary.totalPending.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Main Table Container (Maximized Height, Scrollable, Crisp & Professional) */}
      <div className="flex-1 bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden flex flex-col min-h-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-indigo-600">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-indigo-600"></div>
            <p className="mt-2 text-xs font-bold text-slate-400">Loading ledger data...</p>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
              <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_rgba(0,0,0,0.05)]">
                <tr className="text-slate-500 text-[10px] uppercase font-bold border-b border-slate-200">
                  <th className="py-2.5 px-3.5">Party Name</th>
                  {activeTab === 'CUSTOMER' && <th className="py-2.5 px-3.5">Mobile No</th>}
                  <th className="py-2.5 px-3.5 text-center">Transactions</th>
                  <th className="py-2.5 px-3.5 text-right">Total Amount (Dr ₹)</th>
                  <th className="py-2.5 px-3.5 text-right">Paid (Cr ₹)</th>
                  {activeTab === 'SUPPLIER' && <th className="py-2.5 px-3.5 text-right text-teal-700">Credit / Returns (CN Cr ₹)</th>}
                  <th className="py-2.5 px-3.5 text-right">Pending Due (₹)</th>
                  <th className="py-2.5 px-3.5 text-center print:hidden">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {filteredParties.length > 0 ? (
                  filteredParties.map((party, i) => (
                    <tr 
                      key={party.name + i} 
                      className="hover:bg-indigo-50/40 transition-colors cursor-pointer group"
                      onClick={() => setSelectedParty(party)}
                    >
                      <td className="py-2.5 px-3.5 font-bold text-slate-800 uppercase group-hover:text-indigo-700 transition-colors">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-slate-100 text-slate-600 font-bold text-[11px] flex items-center justify-center shrink-0">
                            {activeTab === 'CUSTOMER' ? '👤' : '🏢'}
                          </div>
                          <span>{party.name}</span>
                        </div>
                      </td>
                      {activeTab === 'CUSTOMER' && (
                        <td className="py-2.5 px-3.5 font-semibold text-slate-500">
                          {party.mobile || '-'}
                        </td>
                      )}
                      <td className="py-2.5 px-3.5 text-center">
                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-bold text-[10px]">
                          {party.transactions.length}
                        </span>
                      </td>
                      <td className="py-2.5 px-3.5 font-black text-slate-800 text-right">
                        {party.totalBilled.toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3.5 font-bold text-emerald-600 text-right">
                        {party.totalPaid.toFixed(2)}
                      </td>
                      {activeTab === 'SUPPLIER' && (
                        <td className="py-2.5 px-3.5 font-black text-teal-600 text-right">
                          {party.totalReturns > 0 ? (
                            <span className="px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded font-bold">
                              ₹{party.totalReturns.toFixed(2)}
                            </span>
                          ) : (
                            '0.00'
                          )}
                        </td>
                      )}
                      <td className="py-2.5 px-3.5 font-black text-right">
                        {party.totalPending > 0 ? (
                          <span className="text-rose-600">₹{party.totalPending.toFixed(2)}</span>
                        ) : (
                          <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-black text-[10px] border border-emerald-300 shadow-sm">
                            ✓ CLEARED (₹0.00)
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3.5 text-center print:hidden">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedParty(party);
                            }}
                            className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-600 hover:text-white text-indigo-700 rounded-lg font-bold text-[11px] transition-colors border border-indigo-200 cursor-pointer"
                          >
                            View Details
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmParty(party);
                            }}
                            className="px-2.5 py-1 bg-rose-50 hover:bg-rose-600 hover:text-white text-rose-600 rounded-lg font-bold text-[11px] transition-colors border border-rose-200 flex items-center gap-1 cursor-pointer"
                            title="Delete Party & All Transactions"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={activeTab === 'CUSTOMER' ? 7 : 7} className="py-12 text-center text-slate-400 font-bold text-xs">
                      No parties found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Popup for Selected Party */}
      {selectedParty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-5xl h-[80vh] flex flex-col shadow-2xl border border-slate-100 animate-in zoom-in-95">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-3xl shrink-0">
              <div>
                <h3 className="text-xl font-black text-slate-800">{selectedParty.name}</h3>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 font-bold mt-1">
                  <span>Total Purchases (Dr): <strong>₹{selectedParty.totalBilled.toFixed(2)}</strong></span>
                  <span>•</span>
                  <span>Paid (Cr): <strong className="text-emerald-600">₹{selectedParty.totalPaid.toFixed(2)}</strong></span>
                  {selectedParty.totalReturns > 0 && (
                    <>
                      <span>•</span>
                      <span>Credit Notes / Returns (Cr): <strong className="text-teal-600">₹{selectedParty.totalReturns.toFixed(2)}</strong></span>
                    </>
                  )}
                  <span>•</span>
                  <span>
                    Net Pending Due: {selectedParty.totalPending === 0 ? (
                      <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-black border border-emerald-300">✓ CLEARED (₹0.00 DUE)</span>
                    ) : (
                      <span className="text-rose-600 font-black">₹{selectedParty.totalPending.toFixed(2)}</span>
                    )}
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedParty(null)}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            {/* Modal Content - Table */}
            <div className="flex-1 overflow-auto p-4">
              <table className="w-full text-left border-collapse whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-sm uppercase tracking-wider font-bold">
                    <th className="p-3 sticky top-0 bg-slate-50">Date</th>
                    {activeTab === 'SUPPLIER' && <th className="p-3 sticky top-0 bg-slate-50">Invoice / CN No</th>}
                    <th className="p-3 sticky top-0 bg-slate-50 max-w-[200px]">Items</th>
                    <th className="p-3 sticky top-0 bg-slate-50">Type</th>
                    <th className="p-3 text-right sticky top-0 bg-slate-50">Amount (₹)</th>
                    <th className="p-3 text-center sticky top-0 bg-slate-50">Status</th>
                    <th className="p-3 text-center sticky top-0 bg-slate-50 print:hidden">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {selectedParty.transactions
                    .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt))
                    .map((tx, i) => {
                      let isPaid = false;
                      let amount = 0;
                      let type = '';
                      const isCN = tx.isCreditNote || Boolean(tx.creditNoteNo);

                      if (isCN) {
                        amount = parseFloat(tx.totalAmount || 0);
                        type = 'CREDIT NOTE (RETURN)';
                        isPaid = true;
                      } else if (activeTab === 'CUSTOMER') {
                        isPaid = tx.paymentStatus === 'PAID' || tx.saleType === 'CASH' || tx.saleType === 'UPI';
                        amount = parseFloat(tx.netAmount || tx.amount || 0);
                        type = tx.saleType || 'CASH';
                      } else {
                        isPaid = tx.paymentType === 'Cash' || tx.paymentType === 'UPI' || tx.status === 'Paid';
                        amount = parseFloat(tx.netAmount || tx.totalAmount || tx.grossAmount || 0);
                        type = tx.paymentType || '-';
                      }

                      const isSettledByCNOrPayment = isPaid || (activeTab === 'SUPPLIER' && selectedParty.totalPending === 0) || (activeTab === 'SUPPLIER' && selectedParty.totalReturns >= amount);

                      return (
                        <tr key={tx.id || i} className={`hover:bg-slate-50 transition-colors ${isCN ? 'bg-teal-50/40' : ''}`}>
                          <td className="p-3 text-xs font-bold text-slate-600">{tx.date}</td>
                          {activeTab === 'SUPPLIER' && (
                            <td className="p-3 text-sm font-black text-slate-700">
                              {isCN ? <span className="text-teal-700">{tx.creditNoteNo}</span> : (tx.invoiceNo || '-')}
                            </td>
                          )}
                          <td className="p-3 text-xs font-bold text-slate-500 max-w-[200px] truncate" title={tx.items ? tx.items.map(it => it.name || it.productName).join(', ') : (tx.reason || '-')}>
                            {tx.items && tx.items.length > 0 ? tx.items.map(it => it.name || it.productName).join(', ') : (tx.reason || '-')}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-md text-xs font-black uppercase tracking-wider ${isCN ? 'bg-teal-100 text-teal-800 border border-teal-300' : type.toUpperCase() === 'CREDIT' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                              {type}
                            </span>
                          </td>
                          <td className={`p-3 text-sm font-black text-right ${isCN ? 'text-teal-700' : 'text-slate-800'}`}>
                            {isCN ? `- ₹${amount.toFixed(2)}` : `₹${amount.toFixed(2)}`}
                          </td>
                          <td className="p-3 text-center">
                            {isCN ? (
                              <span className="text-teal-700 bg-teal-100 px-3 py-1 rounded-full text-xs tracking-wider font-bold border border-teal-200">RETURN CREDITED</span>
                            ) : isPaid ? (
                              <span className="text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full text-xs tracking-wider font-bold">PAID</span>
                            ) : isSettledByCNOrPayment ? (
                              <span className="text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full text-xs tracking-wider font-bold border border-emerald-200">✓ CLEARED (SETTLED)</span>
                            ) : (
                              <span className="text-rose-600 bg-rose-100 px-3 py-1 rounded-full text-xs tracking-wider font-bold">PENDING</span>
                            )}
                          </td>
                          <td className="p-3 text-center print:hidden">
                            <button
                              onClick={() => setDeleteConfirmTx(tx)}
                              className="px-2 py-1 bg-rose-50 hover:bg-rose-600 hover:text-white text-rose-600 rounded-lg font-bold text-[11px] transition-colors border border-rose-200 inline-flex items-center gap-1 cursor-pointer"
                              title="Delete this transaction"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              <span>Delete</span>
                            </button>
                          </td>
                        </tr>
                      );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-3xl shrink-0 flex flex-wrap justify-between items-center gap-2 print:hidden">
              <div className="flex items-center gap-2 flex-wrap">
                <button 
                  onClick={() => window.print()} 
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black rounded-xl shadow-md transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                  <span>🖨️ Print Party Statement</span>
                </button>
                <button
                  onClick={() => setDeleteConfirmParty(selectedParty)}
                  className="px-4 py-2 bg-rose-50 hover:bg-rose-600 text-rose-600 hover:text-white text-xs font-bold rounded-xl border border-rose-200 transition-all flex items-center gap-1.5 cursor-pointer"
                  title="Delete Entire Party and all its transactions"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Delete Entire Party</span>
                </button>
              </div>
              <button onClick={() => setSelectedParty(null)} className="px-6 py-2 bg-slate-800 text-white text-xs font-bold rounded-xl shadow-md hover:bg-slate-700 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Party Confirmation Modal */}
      {deleteConfirmParty && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => !isDeletingParty && setDeleteConfirmParty(null)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-rose-200 animate-in zoom-in-95 duration-200"
          >
            <div className="p-6 bg-gradient-to-r from-rose-600 to-red-700 text-white text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 text-2xl backdrop-blur-xs">
                🗑️
              </div>
              <h3 className="text-xl font-black">Delete Party Records?</h3>
              <p className="text-xs text-rose-100 font-medium mt-1">This action cannot be undone</p>
            </div>

            <div className="p-6 space-y-3.5 text-xs bg-white">
              <div className="p-3.5 bg-rose-50 border border-rose-200/80 rounded-2xl space-y-2 font-bold text-slate-700">
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Party Name:</span>
                  <span className="text-slate-900 font-black uppercase">{deleteConfirmParty.name}</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Ledger Type:</span>
                  <span className="text-slate-900">{activeTab === 'CUSTOMER' ? 'Customer' : 'Supplier'}</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Total Records:</span>
                  <span className="text-slate-900 font-black">{deleteConfirmParty.transactions?.length || 0} Transactions</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Total Amount:</span>
                  <span className="text-slate-900 font-black">₹{deleteConfirmParty.totalBilled.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-1 text-sm font-black text-rose-700">
                  <span>Pending Due:</span>
                  <span>₹{deleteConfirmParty.totalPending.toFixed(2)}</span>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                Deleting this party will delete all associated {activeTab === 'CUSTOMER' ? 'sales invoices' : 'purchase bills and credit notes'} from the database and ledger statements.
              </p>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end items-center gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmParty(null)}
                disabled={isDeletingParty}
                className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl font-bold text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteParty(deleteConfirmParty)}
                disabled={isDeletingParty}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black text-xs shadow-md shadow-rose-600/30 transition-all flex items-center gap-1.5"
              >
                {isDeletingParty ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    <span>Yes, Delete Party</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Single Transaction Confirmation Modal */}
      {deleteConfirmTx && (
        <div 
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => !isDeletingTx && setDeleteConfirmTx(null)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-rose-200 animate-in zoom-in-95 duration-200"
          >
            <div className="p-6 bg-gradient-to-r from-rose-600 to-red-700 text-white text-center">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 text-2xl backdrop-blur-xs">
                🗑️
              </div>
              <h3 className="text-xl font-black">Delete Transaction?</h3>
              <p className="text-xs text-rose-100 font-medium mt-1">This record will be permanently deleted</p>
            </div>

            <div className="p-6 space-y-3.5 text-xs bg-white">
              <div className="p-3.5 bg-rose-50 border border-rose-200/80 rounded-2xl space-y-2 font-bold text-slate-700">
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Date:</span>
                  <span className="text-slate-900">{deleteConfirmTx.date || '-'}</span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Doc / Invoice No:</span>
                  <span className="text-slate-900 font-black">
                    {deleteConfirmTx.creditNoteNo || deleteConfirmTx.invoiceNo || deleteConfirmTx.billNo || deleteConfirmTx.id || '-'}
                  </span>
                </div>
                <div className="flex justify-between border-b border-rose-200/50 pb-1.5">
                  <span className="text-slate-400">Type:</span>
                  <span className="text-slate-900 font-black">
                    {deleteConfirmTx.isCreditNote ? 'Credit Note (Return)' : (deleteConfirmTx.paymentType || deleteConfirmTx.saleType || 'Standard Bill')}
                  </span>
                </div>
                <div className="flex justify-between pt-1 text-sm font-black text-rose-700">
                  <span>Amount:</span>
                  <span>₹{parseFloat(deleteConfirmTx.totalAmount || deleteConfirmTx.netAmount || deleteConfirmTx.amount || deleteConfirmTx.grossAmount || 0).toFixed(2)}</span>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                This transaction will be permanently removed from the ledger and recalculate all balances.
              </p>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end items-center gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmTx(null)}
                disabled={isDeletingTx}
                className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl font-bold text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteTransaction(deleteConfirmTx)}
                disabled={isDeletingTx}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black text-xs shadow-md shadow-rose-600/30 transition-all flex items-center gap-1.5"
              >
                {isDeletingTx ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    <span>Yes, Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerReport;
